import {
    Box,
    Button,
    CircularProgress,
    Grid,
    Pagination,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from 'src/redux-app-hooks';
import { DocumentTemplateDto } from './document-generation.dto';
import { useEffect } from 'react';
import { deleteDocumentTemplate, loadDocumentTemplates } from './document-generation-template.slice';
import { Link, useSearchParams } from 'react-router';
import { asGermanDateTime } from 'src/common/date';
import { DeleteOutlineOutlined, EditOutlined, FileOpenOutlined } from '@mui/icons-material';

export default function DocumentTemplateListPage() {
    const isLoading = useAppSelector((state) => state.documentGenerationTemplate.isLoading);
    const templates = useAppSelector((state) => state.documentGenerationTemplate.templates);

    const [searchParams, setSearchParams] = useSearchParams();

    const dispatch = useAppDispatch();

    const { t } = useTranslation();

    useEffect(() => {
        dispatch(
            loadDocumentTemplates({
                page: Number(searchParams.get('page')) || 1,
                size: Number(searchParams.get('size')) || 10,
                sort: searchParams.get('sort') || 'desc',
            }),
        );
    }, [dispatch, searchParams]);

    function changePage(page: number) {
        setSearchParams((prev) => {
            const params = new URLSearchParams(prev);
            params.set('page', page.toString());
            return params;
        });
    }

    async function deleteTemplate(id?: string) {
        if (!id) {
            console.error('No template ID provided for deletion');
            return;
        }

        if (window.confirm(t('Are you sure you want to delete this template?'))) {
            await dispatch(deleteDocumentTemplate(id));
            dispatch(
                loadDocumentTemplates({
                    page: Number(searchParams.get('page')) || 1,
                    size: Number(searchParams.get('size')) || 10,
                    sort: searchParams.get('sort') || 'desc',
                }),
            );
        }
    }

    return (
        <Grid container direction="row" size={12} spacing={2}>
            <Grid size={12}>
                <Typography variant="h4">{t('Document Templates')}</Typography>
            </Grid>

            <Grid size={12} container direction="row" sx={{ justifyContent: 'right' }}>
                <Link to="/document-generation/templates/create">
                    <Button variant="contained">{t('Create New Template')}</Button>
                </Link>
            </Grid>

            <Grid size={12}>
                {isLoading ? (
                    <CircularProgress />
                ) : (
                    <Table>
                        <TableHead>
                            <TableRow
                                sx={{
                                    backgroundColor: '#fbfcfe',
                                    height: 10,
                                }}
                            >
                                {['Title', 'Created by', 'Created at', 'Actions'].map((label) => (
                                    <TableCell
                                        key={label}
                                        sx={{
                                            fontWeight: 'bold',
                                            color: '#1e293b',
                                            py: 1,
                                            fontSize: '0.875rem',
                                        }}
                                    >
                                        {t(label)}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            {templates?.content.map((template: DocumentTemplateDto) => (
                                <TableRow key={template.id}>
                                    <TableCell>{template.name}</TableCell>
                                    <TableCell>{template.createdBy}</TableCell>
                                    <TableCell>{asGermanDateTime(template.createdAt)}</TableCell>
                                    <TableCell>
                                        <Link to={`/document-generation/templates/${template.id}/edit`}>
                                            <Button variant="outlined" startIcon={<EditOutlined />}>
                                                {t('Edit')}
                                            </Button>
                                        </Link>
                                        <Link to={template.templateUrl} target="_blank" rel="noopener noreferrer">
                                            <Button variant="outlined" sx={{ ml: 1 }} startIcon={<FileOpenOutlined />}>
                                                Google Docs
                                            </Button>
                                        </Link>
                                        <Button
                                            variant="outlined"
                                            color="error"
                                            onClick={() => {
                                                deleteTemplate(template.id);
                                            }}
                                            startIcon={<DeleteOutlineOutlined />}
                                            sx={{ ml: 1 }}
                                        >
                                            {t('Delete')}
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                )}
            </Grid>

            <Grid size={12} container direction="row" sx={{ justifyContent: 'center' }}>
                {!!templates?.content.length && (
                    <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
                        <Pagination
                            variant="outlined"
                            count={templates.totalPages}
                            page={Number(searchParams.get('page') || 1)}
                            onChange={(_, page) => changePage(page)}
                        />
                    </Box>
                )}
            </Grid>
        </Grid>
    );
}
